/**
 * Copyright (c) 2025 Répét Contributors
 * Licensed under the MIT License
 */

import { useUIStore } from '../../state/uiStore'
import type { ErrorType } from '../../state/uiStore'

const toastConfig: Record<ErrorType, { border: string; iconColor: string; icon: JSX.Element }> = {
  error: {
    border: 'border-red-500',
    iconColor: 'text-red-500',
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    ),
  },
  warning: {
    border: 'border-orange-400',
    iconColor: 'text-orange-400',
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
        />
      </svg>
    ),
  },
  info: {
    border: 'border-blue-400',
    iconColor: 'text-blue-400',
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z"
        />
      </svg>
    ),
  },
  success: {
    border: 'border-green-500',
    iconColor: 'text-green-500',
    icon: (
      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
  },
}

/**
 * Composant Toast - Affiche les notifications
 */
export function Toast() {
  const errors = useUIStore((state) => state.errors)
  const removeError = useUIStore((state) => state.removeError)

  if (errors.length === 0) return null

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      aria-live="polite"
      aria-label="Notifications"
    >
      {errors.map((error) => {
        const config = toastConfig[error.type] ?? toastConfig.error
        return (
          <div
            key={error.id}
            className={`min-w-[300px] rounded-lg bg-white p-4 shadow-lg border-l-4 ${config.border}`}
            role={error.type === 'error' ? 'alert' : 'status'}
          >
            <div className="flex items-start gap-3">
              <span className={config.iconColor}>{config.icon}</span>
              <div className="flex-1">
                <p className="text-sm text-gray-900">{error.message}</p>
              </div>
              <button
                onClick={() => removeError(error.id)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Fermer"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
